import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { APP_BAR_HEIGHT, getUserResourceUsageUrl } from '../constants';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import { MONTH_NAMES } from '../constants';
import Loading from '../loading/loading';

const Usage = () => {
    const theme = useTheme();
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);

    // Responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Get current date for defaults
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-based (0 = January)
    const currentYear = currentDate.getFullYear();

    // State for selected month and year
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // State for usage data
    const [usageData, setUsageData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Helper functions for formatting
    const formatStorage = (bytes) => {
        if (bytes === 0) return '0 B';
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    const formatTime = (seconds) => {
        if (seconds === 0) return '0s';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    };

    // Group usage data by title and aggregate totals
    const getGroupedUsageData = () => {
        const grouped = {};

        usageData.forEach(usage => {
            const title = usage.title || 'Untitled';

            if (!grouped[title]) {
                grouped[title] = {
                    title,
                    transcript: usage.transcript,
                    start_date: usage.start_date,
                    storage: { total: 0, time: 0 },
                    processing: { total: 0, time: 0 },
                    transcript_usage: { total: 0, time: 0 }
                };
            }

            const group = grouped[title];

            // Aggregate based on charge type
            switch (usage.charge_type) {
                case 'storage':
                    group.storage.total += usage.storage || 0;
                    group.storage.time += usage.time_used || 0;
                    break;
                case 'processing':
                    group.processing.total += usage.storage || 0;
                    group.processing.time += usage.time_used || 0;
                    break;
                case 'transcript':
                    group.transcript_usage.total += usage.storage || 0;
                    group.transcript_usage.time += usage.time_used || 0;
                    break;
            }

            // Keep the earliest date
            if (!group.start_date || (usage.start_date && new Date(usage.start_date) < new Date(group.start_date))) {
                group.start_date = usage.start_date;
            }
        });

        return Object.values(grouped);
    };

    // Calculate totals across all videos
    const getTotals = () => {
        const groupedData = getGroupedUsageData();
        return groupedData.reduce((totals, group) => {
            totals.storage.total += group.storage.total;
            totals.storage.time += group.storage.time;
            totals.processing.total += group.processing.total;
            totals.processing.time += group.processing.time;
            totals.transcript_usage.total += group.transcript_usage.total;
            totals.transcript_usage.time += group.transcript_usage.time;
            return totals;
        }, {
            storage: { total: 0, time: 0 },
            processing: { total: 0, time: 0 },
            transcript_usage: { total: 0, time: 0 }
        });
    };

    // Generate year options (current year and previous 2 years)
    const yearOptions = [];
    for (let i = currentYear; i >= currentYear - 2; i--) {
        yearOptions.push(i);
    }

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
    };

    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
    };

    // Function to fetch usage data
    const fetchUsageData = async (month, year) => {
        setLoading(true);

        try {
            // Create start and end dates for the selected month
            const startDate = new Date(year, month, 1); // First day of month
            const endDate = new Date(year, month + 1, 0); // Last day of month

            // Format dates as YYYY-MM-DD
            const startDateStr = startDate.toISOString().split('T')[0];
            const endDateStr = endDate.toISOString().split('T')[0];

            const token = userStore.getToken();
            const url = `${getUserResourceUsageUrl}?token=${token}&start_date=${startDateStr}&end_date=${endDateStr}`;

            const response = await fetch(url);
            const data = await response.json();

            if (response.ok) {
                setUsageData(data.usage || []);
            } else {
                messageStore.setMessage(data.error || 'Failed to fetch usage data', 'error');
            }
        } catch (err) {
            messageStore.setMessage('Network error: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when component mounts or when month/year changes
    useEffect(() => {
        if (userStore.getToken()) {
            fetchUsageData(selectedMonth, selectedYear);
        }
    }, [selectedMonth, selectedYear, userStore]);

    return (
        <Container maxWidth="lg" sx={{ py: 4, mt: `${APP_BAR_HEIGHT}px` }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: '60vh'
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        mb: 4,
                        color: theme.palette.text.primary,
                        fontWeight: 'bold',
                        textAlign: 'center'
                    }}
                >
                    Usage
                </Typography>

                {/* Month and Year Picker */}
                <Box sx={{ mb: 4, width: '100%', maxWidth: '400px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel id="month-select-label">Month</InputLabel>
                                <Select
                                    labelId="month-select-label"
                                    id="month-select"
                                    value={selectedMonth}
                                    label="Month"
                                    onChange={handleMonthChange}
                                >
                                    {MONTH_NAMES.map((month, index) => (
                                        <MenuItem key={index} value={index}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel id="year-select-label">Year</InputLabel>
                                <Select
                                    labelId="year-select-label"
                                    id="year-select"
                                    value={selectedYear}
                                    label="Year"
                                    onChange={handleYearChange}
                                >
                                    {yearOptions.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                {/* Selected Date Display */}
                <Typography
                    variant="h5"
                    sx={{
                        mb: 3,
                        color: theme.palette.text.primary,
                        textAlign: 'center'
                    }}
                >
                    Usage for {MONTH_NAMES[selectedMonth]} {selectedYear}
                </Typography>

                {/* Loading State */}
                {loading && <Loading title="Loading usage data..." />}


                {/* Usage Data Display */}
                {!loading && (
                    <Box sx={{ mt: 4, width: '100%' }}>
                        {usageData.length === 0 ? (
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textAlign: 'center',
                                    py: 4
                                }}
                            >
                                No usage data found for {MONTH_NAMES[selectedMonth]} {selectedYear}
                            </Typography>
                        ) : (
                            <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: isMobile ? 300 : 650 }} aria-label="usage data table">
                                    <TableHead>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                            '& .MuiTableCell-root': {
                                                color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                fontWeight: 'bold',
                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                padding: isMobile ? '8px 4px' : '16px'
                                            }
                                        }}>
                                            <TableCell sx={{ minWidth: isMobile ? '100px' : '150px' }}>Title</TableCell>
                                            <TableCell align="right" sx={{ minWidth: isMobile ? '70px' : '100px' }}>
                                                {isMobile ? 'Storage' : 'Storage'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ minWidth: isMobile ? '70px' : '100px' }}>
                                                {isMobile ? 'Process' : 'Processing'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ minWidth: isMobile ? '70px' : '100px' }}>
                                                {isMobile ? 'Transcript' : 'Transcription'}
                                            </TableCell>
                                            {!isMobile && <TableCell sx={{ minWidth: '90px' }}>Date</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getGroupedUsageData().map((group, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.action.selected
                                                    },
                                                    '& .MuiTableCell-root': {
                                                        padding: isMobile ? '8px 4px' : '16px',
                                                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                                                    }
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontWeight: 500,
                                                            lineHeight: 1.2,
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {group.title}
                                                    </Typography>
                                                    {group.transcript && !isMobile && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                display: 'block',
                                                                lineHeight: 1.1,
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            {group.transcript}
                                                        </Typography>
                                                    )}
                                                    {isMobile && group.start_date && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                display: 'block',
                                                                fontSize: '0.65rem'
                                                            }}
                                                        >
                                                            {new Date(group.start_date).toLocaleDateString()}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            color: group.storage.total > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatStorage(group.storage.total)}
                                                    </Typography>
                                                    {group.storage.time > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatTime(group.storage.time)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            color: group.processing.time > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatTime(group.processing.time)}
                                                    </Typography>
                                                    {group.processing.total > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatStorage(group.processing.total)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            color: group.transcript_usage.time > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatTime(group.transcript_usage.time)}
                                                    </Typography>
                                                    {group.transcript_usage.total > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatStorage(group.transcript_usage.total)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {group.start_date ? new Date(group.start_date).toLocaleDateString() : 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}

                                        {/* Totals Row */}
                                        {getGroupedUsageData().length > 0 && (
                                            <TableRow sx={{
                                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                                borderTop: `2px solid ${theme.palette.divider}`,
                                                '& .MuiTableCell-root': {
                                                    fontWeight: 'bold',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    padding: isMobile ? '8px 4px' : '16px'
                                                }
                                            }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        Total ({getGroupedUsageData().length} videos)
                                                    </Typography>
                                                    {isMobile && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                display: 'block',
                                                                fontSize: '0.65rem'
                                                            }}
                                                        >
                                                            {MONTH_NAMES[selectedMonth]} {selectedYear}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 'bold',
                                                            color: theme.palette.text.primary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatStorage(getTotals().storage.total)}
                                                    </Typography>
                                                    {getTotals().storage.time > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatTime(getTotals().storage.time)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 'bold',
                                                            color: theme.palette.text.primary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatTime(getTotals().processing.time)}
                                                    </Typography>
                                                    {getTotals().processing.total > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatStorage(getTotals().processing.total)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant={isMobile ? "caption" : "body2"}
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 'bold',
                                                            color: theme.palette.text.primary,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {formatTime(getTotals().transcript_usage.time)}
                                                    </Typography>
                                                    {getTotals().transcript_usage.total > 0 && !isMobile && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                                            {formatStorage(getTotals().transcript_usage.total)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                                                            {MONTH_NAMES[selectedMonth]} {selectedYear}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default Usage; 